import { useRef, useState } from "react";
import { Camera, X, Star, Images } from "lucide-react";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import CameraCapture from "@/components/CameraCapture";

/**
 * Client-side downscale + JPEG re-encode BEFORE upload. Big phone photos
 * (12 MP JPEGs pushing 8 MB) shrink to ~150-300 KB while staying sharp on a
 * listing card. Falls back to the original file if canvas fails.
 */
async function resizeImage(file, maxEdge = 1920, quality = 0.82) {
  if (!file.type.startsWith("image/")) return file;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = URL.createObjectURL(file);
    });
    const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 1_500_000) return file; // already small
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

/**
 * Multi-photo picker (up to 5). The first item is the primary image. Photos
 * are stored as fully-qualified URLs so the caller can send them straight to
 * the listings endpoint without further processing.
 */
export default function PhotoStrip({ photos, onChange, max = 5, testId = "photo-strip" }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const galleryInputRef = useRef(null);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []).slice(0, max - photos.length);
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const next = [...photos];
      for (const f of files) {
        const smaller = await resizeImage(f);
        const fd = new FormData();
        fd.append("file", smaller);
        const r = await api.post("/upload/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(`${BACKEND_URL}${r.data.url}`);
      }
      onChange(next.slice(0, max));
    } catch (e) {
      setError(formatApiError(e) || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function remove(i) {
    const next = photos.filter((_, idx) => idx !== i);
    onChange(next);
  }

  function makePrimary(i) {
    if (i === 0) return;
    const next = [photos[i], ...photos.filter((_, idx) => idx !== i)];
    onChange(next);
  }

  const canAdd = photos.length < max;
  const primary = photos[0];

  return (
    <div data-testid={testId}>
      {primary ? (
        <div className="relative">
          <img
            src={primary}
            alt="Primary"
            className="w-full aspect-[4/3] object-cover rounded-xl border border-slate-200 bg-slate-100"
          />
          <span className="absolute top-2 left-2 bg-white/95 text-slate-800 text-[11px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-[#E05915] text-[#E05915]" /> Primary
          </span>
          <button
            type="button"
            onClick={() => remove(0)}
            className="absolute top-2 right-2 bg-white/95 hover:bg-white text-slate-700 rounded-full p-1.5 shadow"
            aria-label="Remove primary photo"
            data-testid="remove-primary-photo"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 sm:p-5 text-center">
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            className="inline-flex flex-col items-center justify-center w-full h-40 rounded-lg bg-white border border-slate-200 hover:border-[#E05915] hover:text-[#E05915] text-slate-700 transition-colors"
            data-testid="photo-open-camera"
          >
            <span className="grid place-items-center w-12 h-12 rounded-full bg-[#E05915] text-white mb-2 shadow-md">
              <Camera className="w-6 h-6" aria-hidden="true" />
            </span>
            <span className="text-base font-semibold text-slate-900">Take photos</span>
            <span className="text-xs text-slate-500 mt-0.5">
              Roll end, close-up of the pile, label if you have it
            </span>
          </button>
          <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <span>or</span>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="inline-flex items-center gap-1 font-semibold text-slate-700 hover:text-[#E05915]"
              data-testid="photo-open-gallery"
            >
              <Images className="w-3.5 h-3.5" /> Pick from gallery
            </button>
          </div>
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-primary-input"
          />
        </div>
      )}

      {/* Extra photos row */}
      <div className="mt-3 grid grid-cols-5 gap-2">
        {photos.slice(1).map((p, idx) => (
          <div
            key={p + idx}
            className="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
          >
            <img src={p} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => makePrimary(idx + 1)}
              className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] font-semibold py-1 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`make-primary-${idx + 1}`}
            >
              Set primary
            </button>
            <button
              type="button"
              onClick={() => remove(idx + 1)}
              className="absolute top-1 right-1 bg-white/90 text-slate-700 rounded-full p-0.5"
              aria-label="Remove photo"
              data-testid={`remove-photo-${idx + 1}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-white grid place-items-center hover:bg-slate-50 text-slate-500"
            data-testid="photo-extra-add"
            aria-label="Add another photo"
          >
            <div className="flex flex-col items-center text-[11px]">
              <Camera className="w-5 h-5 mb-0.5" />
              Add
            </div>
          </button>
        )}
      </div>

      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        maxShots={max - photos.length}
        onCapture={(files) => handleFiles(files)}
      />

      {uploading && (
        <div className="text-xs text-slate-500 mt-2" data-testid="photo-uploading">
          Uploading photo…
        </div>
      )}
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 mt-2" data-testid="photo-error">
          {error}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-2">
        Up to {max} photos · Only 1 is required · First image is the primary
      </div>
    </div>
  );
}
